import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth';
import { Loading } from './components/ui';
import { LoginPage } from './pages/Login';
import { HomePage } from './pages/Home';
import { LotCreatePage } from './pages/LotCreate';
import { LotDetailPage } from './pages/LotDetail';
import { FindLotPage } from './pages/FindLot';
import { LotsPage } from './pages/Lots';
import { MoveLotsPage } from './pages/MoveLots';
import { ProductsPage } from './pages/Products';
import { ProductDetailPage } from './pages/ProductDetail';
import { ProductNewPage } from './pages/ProductNew';
import { ProductImportPage } from './pages/ProductImport';
import { ImportHistoryPage, ImportDetailPage } from './pages/ImportHistory';
import { SettingsPage } from './pages/Settings';
import { LabelsPage } from './pages/Labels';
import { WebDataPage } from './pages/WebData';
import { BinDetailPage, BinsPage } from './pages/Bins';

function ConnectionBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  if (online) return null;
  return <div className="offline-banner">No connection. Changes cannot be saved until the connection returns. Your typed entries are kept.</div>;
}

/** Text fields that bring up the on-screen keyboard (not buttons, checkboxes, file pickers or selects). */
const typesText = (el: Element | null) =>
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(el.type));

/**
 * Marks <body> with "keyboard-open" while a text field is focused on a phone and the visible
 * area has shrunk (the keyboard is up), so the menus can get out of the way.
 */
function useKeyboardClass() {
  useEffect(() => {
    const phone = window.matchMedia('(max-width: 899px)');
    const vv = window.visualViewport;
    const update = () => {
      const typing = phone.matches && typesText(document.activeElement);
      // If the keyboard is closed while the field keeps focus (e.g. Android back button), show the menu again.
      const shrunk = !vv || vv.height < window.innerHeight - 120;
      document.body.classList.toggle('keyboard-open', typing && shrunk);
    };
    // Focus changes happen before the keyboard finishes opening; check again shortly after.
    const soon = () => {
      update();
      setTimeout(update, 350);
    };
    document.addEventListener('focusin', soon);
    document.addEventListener('focusout', soon);
    vv?.addEventListener('resize', update);
    return () => {
      document.removeEventListener('focusin', soon);
      document.removeEventListener('focusout', soon);
      vv?.removeEventListener('resize', update);
      document.body.classList.remove('keyboard-open');
    };
  }, []);
}

function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  useKeyboardClass();
  const isAdmin = user?.role === 'admin';
  return (
    <div className="app">
      <header className="topbar">
        <Link to="/" className="brand">
          M&amp;A Inventory
        </Link>
        <nav className="topnav">
          <NavLink to="/lots/new">New lot</NavLink>
          <NavLink to="/find">Find lot</NavLink>
          <NavLink to="/lots" end>
            Lots
          </NavLink>
          <NavLink to="/move">Move</NavLink>
          <NavLink to="/bins">Bins</NavLink>
          <NavLink to="/products">Products</NavLink>
          <NavLink to="/imports">Imports</NavLink>
          <NavLink to="/labels">Labels</NavLink>
          {isAdmin && <NavLink to="/web-data">Costco data</NavLink>}
          <NavLink to="/settings">{isAdmin ? 'Settings' : 'Account'}</NavLink>
        </nav>
        <div className="user">
          <span className="muted">{user?.displayName}</span>
          <button className="btn btn-ghost btn-small" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>
      <ConnectionBanner />
      <main className="main">{children}</main>
      <nav className="bottomnav">
        <NavLink to="/" end>
          <span>🏠</span>Home
        </NavLink>
        <NavLink to="/lots/new">
          <span>➕</span>New lot
        </NavLink>
        <NavLink to="/find">
          <span>🔍</span>Find
        </NavLink>
        <NavLink to="/move">
          <span>📍</span>Move
        </NavLink>
      </nav>
    </div>
  );
}

export function App() {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Starting…" />;
  if (!user) return <LoginPage />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/lots/new" element={<LotCreatePage />} />
        <Route path="/lots/:id" element={<LotDetailPage />} />
        <Route path="/lots" element={<LotsPage />} />
        <Route path="/find" element={<FindLotPage />} />
        <Route path="/move" element={<MoveLotsPage />} />
        <Route path="/bins" element={<BinsPage />} />
        <Route path="/bins/:id" element={<BinDetailPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/new" element={<ProductNewPage />} />
        <Route path="/products/:id" element={<ProductDetailPage />} />
        <Route path="/imports" element={<ImportHistoryPage />} />
        <Route path="/imports/new" element={<ProductImportPage />} />
        <Route path="/imports/:id" element={<ImportDetailPage />} />
        <Route path="/labels" element={<LabelsPage />} />
        <Route path="/web-data" element={<WebDataPage />} />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
